/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: sms/hooks/useSmsProvidersList.ts
 🗓️ Created: 2026-01-20
 🧩 Description:
    Custom hook for SMS providers list management - matching Extensions pattern.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchProviders } from '../api';
import type { SmsProvider } from '../types';

interface Column {
    key: string;
    label: string;
    sortable: boolean;
    visible: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    render?: (value: any, item: SmsProvider) => React.ReactNode;
}

interface UseSmsProvidersListOptions {
    initialColumns: Column[];
    initialSortBy?: string;
    initialSortOrder?: 'asc' | 'desc';
    initialLimit?: number;
}

export function useSmsProvidersList({
    initialColumns,
    initialSortBy = 'name',
    initialSortOrder = 'asc',
    initialLimit = 10,
}: UseSmsProvidersListOptions) {
    const [data, setData] = useState<SmsProvider[]>([]);
    const [loading, setLoading] = useState(true);
    const [columns, setColumns] = useState<Column[]>(initialColumns);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState(initialSortBy);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initialSortOrder);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(initialLimit);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const providers = await fetchProviders();
            setData(providers);
        } catch (error) {
            console.error('Error loading SMS providers:', error);
            setData([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Filter and sort data
    const filteredData = useMemo(() => {
        let result = [...data];

        // Apply search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            result = result.filter(item =>
                item.name.toLowerCase().includes(query) ||
                item.providerType.toLowerCase().includes(query)
            );
        }

        // Apply sorting
        result.sort((a, b) => {
            const aVal = (a as any)[sortBy];
            const bVal = (b as any)[sortBy];

            if (typeof aVal === 'string') {
                const cmp = aVal.localeCompare(bVal);
                return sortOrder === 'asc' ? cmp : -cmp;
            }

            return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
        });

        return result;
    }, [data, searchQuery, sortBy, sortOrder]);

    // Paginate data
    const paginatedData = useMemo(() => {
        const start = (page - 1) * limit;
        return filteredData.slice(start, start + limit);
    }, [filteredData, page, limit]);

    const pagination = useMemo(() => ({
        page,
        limit,
        total: filteredData.length,
        totalPages: Math.ceil(filteredData.length / limit),
    }), [page, limit, filteredData.length]);

    const handleSearch = useCallback((query: string) => {
        setSearchQuery(query);
        setPage(1);
    }, []);

    const handleSort = useCallback((key: string) => {
        if (sortBy === key) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(key);
            setSortOrder('asc');
        }
    }, [sortBy]);

    const handlePageChange = useCallback((newPage: number) => {
        setPage(newPage);
    }, []);

    const handleItemsPerPageChange = useCallback((newLimit: number) => {
        setLimit(newLimit);
        setPage(1);
    }, []);

    const handleColumnToggle = useCallback((key: string) => {
        setColumns(prev =>
            prev.map(col =>
                col.key === key ? { ...col, visible: !col.visible } : col
            )
        );
    }, []);

    const refresh = useCallback(() => {
        loadData();
    }, [loadData]);

    return {
        data: paginatedData,
        loading,
        columns,
        pagination,
        searchQuery,
        sortBy,
        sortOrder,
        handleSearch,
        handleSort,
        handlePageChange,
        handleItemsPerPageChange,
        handleColumnToggle,
        refresh,
    };
}
